import { isCaptionTargetLanguage } from './caption-translation-languages';

/**
 * 제목 번역 편집 판단에 필요한 중립 상태. Remotion item 구조에는 의존하지 않는다.
 */
export interface TitleTranslationEditSnapshot {
  displayedText: string;
  sourceText: string;
  displayLanguage?: string;
  translatedLanguage?: string;
  hasStoredTranslation: boolean;
}

/** feature adapter가 실제 item 필드에 펼쳐 넣을 중립 patch. */
export interface TitleTranslationEditPatch {
  sourceText?: string;
  translatedText?: string;
  displayLanguage?: 'source';
  clearStoredTranslation?: true;
}

const LATIN_SCRIPT_PATTERN = /\p{Script=Latin}/u;
const LATIN_WORD_RUN_PATTERN = /\p{Script=Latin}+/gu;
const COMPLETED_HANGUL_PATTERN = /[가-힣]/;
const NON_LATIN_SCRIPT_EVIDENCE_PATTERN =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Cyrillic}\p{Script=Arabic}\p{Script=Hebrew}\p{Script=Devanagari}\p{Script=Thai}]/u;
const NON_KOREAN_SCRIPT_EVIDENCE_PATTERN =
  /[\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Cyrillic}\p{Script=Arabic}\p{Script=Hebrew}\p{Script=Devanagari}\p{Script=Thai}]/u;

/**
 * v1(en/ko) 제목을 다른 언어의 새 원문으로 볼 수 있는 명확한 script 증거만 찾는다.
 *
 * 언어 감지가 아니라 보수적인 heuristic이다. ko 타겟의 Latin은 word run이 3개 이상인
 * 문장만 비타겟으로 보고, 1~2개인 브랜드명·제품명은 보호한다. en 타겟의 Latin 혼합과
 * 미지원 언어 marker도 의도적으로 유지한다.
 */
export function hasClearNonTargetTitleScriptEvidence(
  title: string,
  targetLanguage: string,
): boolean {
  if (!isCaptionTargetLanguage(targetLanguage)) return false;

  const hasLatin = LATIN_SCRIPT_PATTERN.test(title);
  const hasCompletedHangul = COMPLETED_HANGUL_PATTERN.test(title);

  if (targetLanguage === 'en') {
    return (
      hasCompletedHangul ||
      (!hasLatin && NON_LATIN_SCRIPT_EVIDENCE_PATTERN.test(title))
    );
  }

  const latinWordRunCount = title.match(LATIN_WORD_RUN_PATTERN)?.length ?? 0;
  if (!hasCompletedHangul && latinWordRunCount >= 3) return true;

  return (
    !hasCompletedHangul &&
    !hasLatin &&
    NON_KOREAN_SCRIPT_EVIDENCE_PATTERN.test(title)
  );
}

/** 제목 편집을 원문/번역본 중 어디에 저장할지 결정하는 순수 상태 전이. */
export function buildTitleTranslationEditPatch(
  snapshot: TitleTranslationEditSnapshot,
  nextText: string,
): TitleTranslationEditPatch {
  const displayLanguage = snapshot.displayLanguage ?? 'source';

  if (!snapshot.hasStoredTranslation) {
    if (snapshot.displayLanguage === 'source') {
      return { sourceText: nextText };
    }
    if (
      displayLanguage === 'source' ||
      nextText === snapshot.displayedText ||
      !hasClearNonTargetTitleScriptEvidence(nextText, displayLanguage)
    ) {
      return {};
    }
    return { displayLanguage: 'source' };
  }

  if (displayLanguage === 'source') {
    return nextText === snapshot.sourceText
      ? { sourceText: nextText }
      : {
          sourceText: nextText,
          displayLanguage: 'source',
          clearStoredTranslation: true,
        };
  }

  const targetLanguage = snapshot.translatedLanguage ?? displayLanguage;
  if (hasClearNonTargetTitleScriptEvidence(nextText, targetLanguage)) {
    return {
      sourceText: nextText,
      displayLanguage: 'source',
      clearStoredTranslation: true,
    };
  }

  return { translatedText: nextText };
}
